import { isEmpty } from 'lodash-es';
import moment from 'moment-timezone';
import conflictDetectorWorker from '@/location/conflict-detector.worker';
import { useStore } from '@/core/store';
import { useRouter } from '@/core/routes';
import { useI18N } from '@/core/shared/localization';
import deepmerge from 'deepmerge';

const conflictDetector = conflictDetectorWorker();

export default function useActionSaveCancel (locationId, booking, bookings, bookingChanges, startTime, bookingStationTimes, bookingStationTimesChanges, saving) {
  const store = useStore();
  const router = useRouter();
  const i18n = useI18N();

  function returnToOverview () {
    router.push({ name: 'overview', params: { locationId } });
  }

  async function storeBooking () {
    if (isEmpty(bookingChanges.value) && isEmpty(bookingStationTimesChanges.value)) {
      returnToOverview();
      return;
    }

    saving.value = true;

    const payload = {
      startTime: startTime.value,
      ...bookingChanges.value,
    };

    if (booking.value._id !== 'new') {
      payload.id = booking.value._id;
    }

    if (!isEmpty(bookingStationTimesChanges.value)) {
      const computedBookingStationTimes = bookingStationTimes.value.filter(bookingStationTime => !bookingStationTime._removed).map(bookingStationTime => {
        let bookingStationTimeReturn = {};

        if (!bookingStationTime._id.includes('new')) {
          bookingStationTimeReturn.id = bookingStationTime._id;
        }

        if (bookingStationTimesChanges.value[bookingStationTime._id]) {
          bookingStationTimeReturn = {
            ...bookingStationTimeReturn,
            ...bookingStationTimesChanges.value[bookingStationTime._id],
          };
        }

        if (bookingChanges.value._startNow) {
          bookingStationTimeReturn.startedAt = startTime.value;
        }

        if (!bookingStationTime.startedAt) {
          bookingStationTimeReturn.endTime = moment(startTime.value)
            .add(bookingStationTime.tier.length, 'minutes')
            .format();
        }

        return bookingStationTimeReturn;
      });
      payload.bookingStationTimes = computedBookingStationTimes;
    }

    if (bookingChanges.value._startNow) {
      payload.checkedInAt = startTime.value;

      const bookingWithCorrectStartTime = {
        ...booking.value,
        bookingStationTimes: booking.value.bookingStationTimes.map((bookingStationTime) => {
          return {
            ...bookingStationTime,
            startedAt: startTime.value,
          };
        }),
      };

      const bookingsWithUpdatedEndTimes = await conflictDetector.getBookingsWithUpdatedEndTimesForStarting(
        deepmerge({}, bookingWithCorrectStartTime),
        bookings.value.map(booking => { return deepmerge({}, booking); }),
      );

      const bookingsWithJustEndTimes = bookingsWithUpdatedEndTimes.reduce((bookingsAccumulator, booking) => {
        return [...bookingsAccumulator, {
          id: booking.id,
          bookingStationTimes: booking.bookingStationTimes.map(bookingStationTime => ({
            id: bookingStationTime.id,
            endTime: bookingStationTime.endTime,
          })),
        }];
      }, []);

      bookingsWithJustEndTimes.forEach(booking => store.dispatch('storeBooking', booking));
    }

    try {
      store.dispatch('storeBooking', payload);
      returnToOverview();
    } finally {
      saving.value = false;
    }
  }

  function cancelBooking () {
    store.commit('setConfirm', {
      message: i18n.t('Are you sure you would like to delete this reservation?'),
      buttons: [
        {
          name: i18n.t('No'),
          type: 'cancel',
        },
        {
          name: i18n.t('Yes'),
          action: () => {
            router.push({ name: 'overview' });
            store.dispatch('deleteBooking', booking.value);
          },
        },
      ],
    });
  }

  return {
    storeBooking,
    cancelBooking,
  };
}
